import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as DescriptionIcon } from "@common/assets/images/filter-icons/description-icon.svg";
import { ReactComponent as JiraIcon } from "@common/assets/images/integrations/jira-icon.svg";
import { ReactComponent as LinearIcon } from "@common/assets/images/integrations/linear-icon.svg";
import { ReactComponent as NoteIcon } from "@common/assets/images/note-write-icon.svg";
import { ReactComponent as NewMessageIcon } from "@common/assets/images/shared-icons/new-activity-chat.svg";
import MessageInput from "@common/components/Input/MessageInput/MessageInput";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { ToggleButton } from "@common/components/ToggleButton/ToggleButton";
import { App, AppUserShared, getUserInitials, Workspace } from "@common/models";
import { CrashActivity, CrashActivityType } from "@common/models/crashReporting";
import { resolveCreationTimeString } from "@common/models/helpers/time/creationTime";
import {
  ActivityType,
  IssueChat,
  IssueChatRequest,
  IssueMessage,
  IssueMessageType,
} from "@common/models/issueTracking";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { vars } from "@common/styles";
import { docLink } from "@common/util/docs";
import identifiers from "@common/util/identifiers.json";
import { t } from "@common/util/translator";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useUnreadNotificationsContext } from "@main/context/App/UnreadNotificationsContext";
import { useAuthContext } from "@main/context/Auth/Auth.context";
import * as Icon from "@main/pages/shared/icons/icons";
import { RoutePaths } from "@main/router/config/routePaths";
import Linkify from "linkify-react";
import { generatePath, useNavigate } from "react-router-dom";

import ChatMessage from "./ChatMessage";
import { DuplicateActivityText } from "./DuplicateActivityText";
import * as Styled from "./MessageContainer.styles";

interface Props {
  messages?: IssueMessage[];
  activity?: CrashActivity[];
  onMessageSend?: (message: IssueChatRequest) => Promise<void>;
  onMessageDelete?: (message: IssueMessage) => void;
  onMessageEdit?: (message: IssueChat) => Promise<void>;
  appUser?: AppUserShared;
}

export default function MessageContainer({
  messages,
  activity,
  onMessageSend,
  onMessageDelete,
  onMessageEdit,
  appUser,
}: Props) {
  const navigate = useNavigate();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { unreadNotifications } = useUnreadNotificationsContext();
  const [sendToUser, setSendToUser] = useState(false);
  const [editing, setEditing] = useState<IssueChat>();
  const [message, setMessage] = useState<string>("");
  const { userData } = useAuthContext();
  const [unreadMessage, setUnreadMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const toggleSendToUser = useCallback(() => setSendToUser((prev) => !prev), []);

  const sendMessage = useCallback(
    async (message: string) => {
      if (message.trim().length === 0) return;
      setLoading(true);
      await onMessageSend?.({ message: message.trim(), is_note: !sendToUser });
      setLoading(false);
    },
    [onMessageSend, sendToUser],
  );

  const editMessage = useCallback(
    async (newValue: string) => {
      if (!editing) return;
      if (newValue.trim().length === 0) {
        onMessageDelete?.(editing);
      } else {
        setLoading(true);
        await onMessageEdit?.({ ...editing, message: newValue });
        setLoading(false);
      }
      setEditing(undefined);
    },
    [editing, onMessageDelete, onMessageEdit],
  );

  const messageEditable = useCallback(
    (senderId: string) => {
      if (!userData) return false;
      return senderId === userData.id;
    },
    [userData],
  );

  const onEditMessageClick = useCallback((message: IssueChat) => {
    setEditing(message);
    setMessage(message.message);
  }, []);

  const cleanedActivity = useMemo(
    () => activity?.map((ac) => ({ ...ac, activity_description: ac.activity_description.replace(/"|'/g, "") })),
    [activity],
  );

  const cleanedMessages = useMemo(
    () =>
      messages?.map((msg) =>
        msg.type === IssueMessageType.ACTIVITY
          ? { ...msg, activity_description: msg.activity_description.replace(/"|'/g, "") }
          : msg,
      ),
    [messages],
  );

  const showCommonCommentsSettings = () => {
    if (selectedWorkspace)
      navigate(
        generatePath(RoutePaths.ADMINISTRATION_COMMON_COMMENTS, {
          workspaceSlug: selectedWorkspace?.slug,
        }),
        { replace: true },
      );
  };

  useEffect(() => {
    if (selectedApp) {
      messages?.find((message) => {
        const notification = unreadNotifications[selectedApp.id]?.find((n) => {
          return n.subject_id === message.id;
        });
        if (notification) {
          setUnreadMessage(message.id);
          return true;
        }
      });
    }
    // eslint-disable-next-line
  }, [messages, selectedApp]);

  return (
    <Styled.MessageContainerWrap>
      {cleanedActivity &&
        cleanedActivity.map((ac) => {
          return (
            <Styled.SystemMessage key={ac.id}>
              {getActivityIcon(ac.activity_type, ac.activity_description)}
              {getActivityText(ac.activity_type, ac.activity_description, ac.created, ac.activity_url ?? undefined)}
            </Styled.SystemMessage>
          );
        })}

      {cleanedMessages && (
        <Fragment>
          {!cleanedMessages.find((a) => a.type === IssueMessageType.ACTIVITY && a.activity_type === "Rule") &&
            noRulesTriggered(selectedWorkspace, selectedApp)}

          {cleanedMessages.map((message) => {
            switch (message.type) {
              case IssueMessageType.CHAT:
                if (message.is_note && message.user.name !== "Shake AI") {
                  return (
                    <Styled.SystemMessage
                      key={message.id}
                      style={{ alignItems: "flex-start" }}
                    >
                      <div
                        style={{
                          width: "2.4rem",
                          height: "2.4rem",
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        <NoteIcon />
                      </div>
                      <Paragraph
                        color={vars.colors.grey50}
                        fontSize={14}
                      >
                        {message.user.name} left an internal note{" "}
                        <Linkify options={{ target: "_blank" }}>
                          <b>{message.message}</b>
                        </Linkify>
                        &nbsp;&nbsp;&nbsp;
                        <Styled.Date>{resolveCreationTimeString(message.created)}</Styled.Date>
                        {messageEditable(message.user.id) && (
                          <>
                            <Styled.ActionButton onClick={() => onEditMessageClick(message)}>
                              {"Edit"}
                            </Styled.ActionButton>
                            <Styled.ActionButton onClick={() => onMessageDelete?.(message)}>
                              {"Delete"}
                            </Styled.ActionButton>
                          </>
                        )}
                      </Paragraph>
                    </Styled.SystemMessage>
                  );
                }
                return (
                  message.user.name !== "Shake AI" && (
                    <>
                      {unreadMessage === message.id && (
                        <Styled.UnreadFlag>
                          <NewMessageIcon />
                          Unread
                        </Styled.UnreadFlag>
                      )}
                      <ChatMessage
                        key={message.id}
                        user={message.user}
                        isRead={message.is_read}
                        messageContent={message.message}
                        messageTimestamp={resolveCreationTimeString(message.created)}
                        appUser={appUser}
                      />
                    </>
                  )
                );
              case IssueMessageType.ACTIVITY:
                return (
                  <Styled.SystemMessage key={message.id}>
                    <div style={{ width: "2.4rem", height: "2.4rem", marginRight: "1rem" }}>
                      {getActivityIcon(message.activity_type, message.activity_description)}
                    </div>
                    {getActivityText(
                      message.activity_type,
                      message.activity_description,
                      message.created,
                      message.activity_url,
                    )}
                  </Styled.SystemMessage>
                );
            }
          })}
          <Styled.MessageInputWrap>
            <MessageInput
              autoFocus={!!editing}
              key={editing ? editing.id : undefined}
              value={message}
              onChange={setMessage}
              onCommonCommentClick={(comment) => {
                setMessage(message ? message + "\n" + comment : comment);
              }}
              onCommonCommentSettingsClick={showCommonCommentsSettings}
              placeholder={
                editing
                  ? "Edit message"
                  : sendToUser
                  ? t("userFeedbackDetailsPage.chat.replyPlaceholder")
                  : t("userFeedbackDetailsPage.chat.notePlaceholder")
              }
              onMessageSend={editing ? editMessage : sendMessage}
              testId={identifiers["central.column.comment.input"]}
              icontestid={identifiers["central.column.comment.icon.send"]}
              sendButtonText={editing ? "Save" : sendToUser ? "Send" : "Add note"}
              sendingButtonText={editing ? "Saving..." : sendToUser ? "Sending..." : "Adding..."}
              loading={loading}
              sendToUserButton={
                <Styled.ToggleWrap>
                  <Styled.ToggleLabel>Send to app user</Styled.ToggleLabel>
                  <Styled.Tooltip
                    text="Register your app users to chat with them"
                    disabled={!!appUser || loading}
                    position="top"
                  >
                    <ToggleButton
                      checked={sendToUser}
                      onChange={toggleSendToUser}
                      testId={identifiers["central.column.comment.toggle.sendToUser"]}
                      disabled={!appUser || loading}
                    />
                  </Styled.Tooltip>
                </Styled.ToggleWrap>
              }
            />
            {!appUser && (
              <Styled.HelperText>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={docLink(selectedApp?.platform.name).registerAppUser}
                >
                  Register your app users
                </a>{" "}
                to enable live chat.
              </Styled.HelperText>
            )}
          </Styled.MessageInputWrap>
        </Fragment>
      )}
    </Styled.MessageContainerWrap>
  );
}

const getActivityIcon = (activityType: ActivityType | CrashActivityType, message?: string) => {
  if (activityType === ActivityType.RULE) {
    return <Styled.RuleIcon />;
  }
  if (activityType === ActivityType.TITLE) {
    return <DescriptionIcon />;
  }
  if (activityType === ActivityType.TAG) return <Icon.TagsIcon />;
  if (activityType === ActivityType.JIRA) return <JiraIcon />;
  if (activityType === ActivityType.LINEAR) return <LinearIcon />;
  if (activityType === ActivityType.DUPLICATE) return <Icon.DuplicateIcon />;
  if (activityType === ActivityType.CLEAR_DUPLICATE) return <Icon.ClearDuplicateIcon />;

  if (!message) return;
  if (activityType === ActivityType.ASSIGNEE) {
    const userNameArray = message.trimStart().trimEnd().split(" ");
    const userName = `${
      userNameArray[userNameArray.length - 2] !== "to" ? userNameArray[userNameArray.length - 2] : ""
    } ${userNameArray[userNameArray.length - 1]}`;
    return (
      <div style={{ marginRight: "0.8rem" }}>
        <Styled.RoundedCanvas
          size="xsmall"
          letter={!userName.includes("Unassigned") ? getUserInitials(userName) : undefined}
          bgColor={userName.includes("Unassigned") ? vars.colors.grey100 : vars.colors.lavender}
        />
      </div>
    );
  }
  if (activityType === ActivityType.STATUS) {
    if (message.endsWith(TicketStatus.IN_PROGRESS)) return <Icon.InProgressIcon />;
    if (message.endsWith(TicketStatus.NEW)) return <Icon.StatusNewIcon />;
    if (message.endsWith(TicketStatus.CLOSED)) return <Icon.StatusDoneIcon />;
    if (message.endsWith(TicketStatus.LOCKED)) return <Icon.LockedIcon />;
  }
  if (activityType === ActivityType.PRIORITY) {
    if (message.endsWith(TicketPriority.HIGH)) return <Icon.PriorityHighIcon />;
    if (message.endsWith(TicketPriority.LOW)) return <Icon.PriorityLowIcon />;
    if (message.endsWith(TicketPriority.MEDIUM)) return <Icon.PriorityMediumIcon />;
  }
};

const getActivityText = (
  activityType: ActivityType | CrashActivityType,
  message: string,
  created: string,
  url?: string,
) => {
  if (activityType === ActivityType.RULE && url) {
    // eslint-disable-next-line
    const [first, ...rest] = message.split(" ");
    const name = rest.join(" ").split(" triggered");

    return (
      <Paragraph
        fontSize={14}
        color={vars.colors.grey50}
      >
        Rule{" "}
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          {name}
        </a>{" "}
        was triggered.&nbsp;&nbsp;&nbsp;
        <Styled.Date>{resolveCreationTimeString(created)}</Styled.Date>
      </Paragraph>
    );
  }
  if (activityType === ActivityType.DUPLICATE || activityType === ActivityType.CLEAR_DUPLICATE) {
    return (
      <DuplicateActivityText
        description={message}
        url={url}
        created={created}
      />
    );
  }
  return (
    <Fragment>
      <Paragraph
        color={vars.colors.grey50}
        fontSize={14}
      >
        {message}.&nbsp;&nbsp;&nbsp;
        <Styled.Date>{resolveCreationTimeString(created)}</Styled.Date>
      </Paragraph>
    </Fragment>
  );
};

const noRulesTriggered = (selectedWorkspace?: Workspace, selectedApp?: App) => {
  if (!selectedWorkspace || !selectedApp) return;

  return (
    <Styled.SystemMessage>
      {getActivityIcon(ActivityType.RULE)}
      <Paragraph
        fontSize={14}
        color={vars.colors.grey50}
      >
        No{" "}
        <a
          href={generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL_KEY, {
            workspaceSlug: selectedWorkspace?.slug,
            appKey: selectedApp?.key,
          })}
          target="_blank"
          rel="noreferrer"
        >
          Rules
        </a>{" "}
        were triggered.
      </Paragraph>
    </Styled.SystemMessage>
  );
};
